import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, ExternalLink, RefreshCw, Copy, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "../../components/layout/Header";

const GROUPS = [
  {
    title: "Inteligência Artificial",
    items: [
      {
        id: "openai",
        label: "OpenAI",
        desc: "Geração de imagens (gpt-image-1)",
        envKey: "OPENAI_API_KEY",
        doc: "platform.openai.com/api-keys",
      },
      {
        id: "anthropic",
        label: "Anthropic (Claude)",
        desc: "Tradução e reescrita de texto",
        envKey: "ANTHROPIC_API_KEY",
        doc: "console.anthropic.com/settings/keys",
      },
      {
        id: "google",
        label: "Google (Gemini)",
        desc: "Fallback para geração de imagens",
        envKey: "GOOGLE_API_KEY",
        doc: "aistudio.google.com/apikey",
      },
    ],
  },
  {
    title: "Pagamentos",
    items: [
      {
        id: "stripe",
        label: "Stripe — API Key",
        desc: "Checkout e pagamentos com cartão",
        envKey: "STRIPE_API_KEY",
        doc: "dashboard.stripe.com/apikeys",
      },
      {
        id: "stripe_webhook",
        label: "Stripe — Webhook Secret",
        desc: "Validação dos webhooks de pagamento",
        envKey: "STRIPE_WEBHOOK_SECRET",
        doc: "dashboard.stripe.com/webhooks",
      },
      {
        id: "mercadopago",
        label: "MercadoPago",
        desc: "Checkout para pagamentos nacionais",
        envKey: "MERCADOPAGO_TOKEN",
        doc: "mercadopago.com.br/developers/panel",
      },
    ],
  },
  {
    title: "Vídeo & Conteúdo",
    items: [
      {
        id: "heygen",
        label: "HeyGen",
        desc: "Geração de vídeos com avatar da Tayane",
        envKey: "HEYGEN_API_KEY",
        doc: "app.heygen.com/settings/api",
      },
      {
        id: "apify",
        label: "Apify",
        desc: "Ouvintes mensais do Spotify (scraper)",
        envKey: "APIFY_TOKEN",
        doc: "console.apify.com/account/integrations",
      },
    ],
  },
  {
    title: "Analytics & KPIs",
    items: [
      {
        id: "youtube",
        label: "YouTube Data API",
        desc: "Métricas do canal (inscritos, views)",
        envKey: "YOUTUBE_API_KEY",
        doc: "console.cloud.google.com/apis",
      },
      {
        id: "trendsmcp",
        label: "TrendsMCP",
        desc: "Tendências Google, TikTok, YouTube",
        envKey: "TRENDSMCP_API_KEY",
        doc: "api.trendsmcp.ai",
      },
    ],
  },
  {
    title: "Hotmart",
    items: [
      {
        id: "hotmart_client_id",
        label: "Hotmart — Client ID",
        desc: "Autenticação OAuth2 da API Hotmart",
        envKey: "HOTMART_CLIENT_ID",
        doc: "app-vlc.hotmart.com/tools/credentials",
      },
      {
        id: "hotmart_secret",
        label: "Hotmart — Client Secret",
        desc: "Autenticação OAuth2 da API Hotmart",
        envKey: "HOTMART_CLIENT_SECRET",
        doc: "app-vlc.hotmart.com/tools/credentials",
      },
      {
        id: "hotmart_basic",
        label: "Hotmart — Basic Auth",
        desc: "Header Authorization para obter token",
        envKey: "HOTMART_BASIC_AUTH",
        doc: "app-vlc.hotmart.com/tools/credentials",
      },
    ],
  },
];

export default function ApiKeysAdmin() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/api-keys");
      setStatus(data);
    } catch {
      setStatus({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyEnvKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const total = status ? Object.values(status).length : 0;
  const configured = status ? Object.values(status).filter(v => v.ok).length : 0;

  return (
    <div>
      <Header
        title="Chaves de API"
        subtitle="Status das integrações externas — configuradas via .env no servidor"
      />

      {/* Resumo */}
      <div className="flex items-center gap-4 mb-8">
        <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: "12px 20px" }}
          className="flex items-center gap-3">
          <span style={{ fontSize: 28, fontWeight: 700, color: "#111827" }}>{configured}</span>
          <span style={{ fontSize: 13, color: "#6B7280" }}>de {total} configuradas</span>
        </div>
        <button onClick={load} disabled={loading}
          className="admin-btn-secondary" style={{ height: 44 }}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Atualizar
        </button>
      </div>

      {/* Aviso de como configurar */}
      <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "14px 18px", marginBottom: 28, maxWidth: 720 }}>
        <p style={{ fontSize: 13, color: "#92400E", margin: 0 }}>
          <strong>Como configurar:</strong> edite <code style={{ background: "#FEF3C7", padding: "1px 5px", borderRadius: 4 }}>/opt/tayane/backend/.env</code> no servidor
          e reinicie o container backend. As chaves nunca ficam armazenadas no banco de dados.
        </p>
      </div>

      {/* Grupos */}
      <div className="space-y-8 max-w-3xl">
        {GROUPS.map(group => (
          <div key={group.title}>
            <h2 style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.06em", color: "#6B7280", textTransform: "uppercase", marginBottom: 12 }}>
              {group.title}
            </h2>
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
              {group.items.map((item, i) => {
                const s = status?.[item.id];
                const isLast = i === group.items.length - 1;
                return (
                  <div key={item.id} style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    gap: 16,
                    padding: "14px 18px",
                    background: "#FFFFFF",
                    borderBottom: isLast ? "none" : "1px solid #F3F4F6",
                  }}>
                    {/* Esquerda */}
                    <div className="flex items-center gap-3">
                      {s?.ok
                        ? <CheckCircle2 size={17} color="#16A34A" style={{ flexShrink: 0 }} />
                        : <XCircle size={17} color="#D1D5DB" style={{ flexShrink: 0 }} />}
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}>{item.label}</div>
                        <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1 }}>{item.desc}</div>
                      </div>
                    </div>

                    {/* Direita */}
                    <div className="flex items-center gap-3">
                      {s?.ok
                        ? <code style={{ fontSize: 12, background: "#F0FDF4", color: "#15803D", padding: "3px 8px", borderRadius: 6, border: "1px solid #BBF7D0" }}>
                            {s.masked}
                          </code>
                        : <span style={{ fontSize: 12, color: "#9CA3AF" }}>Não configurada</span>}

                      {/* copiar nome da env var */}
                      <button
                        onClick={() => copyEnvKey(item.envKey)}
                        title={`Copiar ${item.envKey}`}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4, borderRadius: 4 }}
                      >
                        {copied === item.envKey ? <Check size={13} color="#16A34A" /> : <Copy size={13} />}
                      </button>

                      <a href={`https://${item.doc}`} target="_blank" rel="noreferrer"
                        style={{ color: "#9CA3AF" }} title={item.doc}>
                        <ExternalLink size={13} />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Instrução de restart */}
      <div style={{ marginTop: 32, maxWidth: 720, background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: "14px 18px" }}>
        <p style={{ fontSize: 12, color: "#6B7280", margin: 0, fontFamily: "monospace" }}>
          # Após editar .env:<br />
          cd /opt/tayane && docker compose cp backend/.env backend:/app/.env && docker compose restart backend
        </p>
      </div>
    </div>
  );
}
